import axios from "axios";
import envConfig from "../../bin/env.config";

const FETCH_PAGE_SIZE = 500;

export const TREE_ROOT_KEY = "root";

/**
 * @param {number | null} parentId
 * @returns {string | number}
 */
export function parentToCacheKey(parentId) {
  if (parentId == null || parentId === "" || parentId === "root") {
    return TREE_ROOT_KEY;
  }
  return Number(parentId);
}

/**
 * @param {string | number} key
 * @returns {number | null}
 */
export function cacheKeyToParentId(key) {
  if (key === TREE_ROOT_KEY) return null;
  return Number(key);
}

/**
 * @param {object[]} products
 */
function sortBySortOrderThenNameThenId(products) {
  return [...products].sort((a, b) => {
    const oa = a.sort_order != null ? Number(a.sort_order) : 0;
    const ob = b.sort_order != null ? Number(b.sort_order) : 0;
    if (oa !== ob) return oa - ob;
    const na = (a.name || "").toString().toLowerCase();
    const nb = (b.name || "").toString().toLowerCase();
    if (na < nb) return -1;
    if (na > nb) return 1;
    return a.id - b.id;
  });
}

/**
 * Fetch all direct children of a parent in one or more paged calls.
 * - parentId == null: send `parent_id: null` in body → only root-level items (API lazy mode).
 * - parentId = N: send `parent_id: N` → only direct children of N.
 *
 * @param {object} params
 * @param {string|number} params.projectId
 * @param {string|object} params.token
 * @param {number | null} params.parentId
 * @param {string} params.orderBy
 * @param {string} params.order
 * @param {string} params.searchText
 * @param {boolean} params.notMappedYet
 * @param {number|string} params.searchIsHidden
 * @param {AbortSignal} [params.signal]
 * @returns {Promise<object[]>}
 */
export async function fetchChildrenForParent({
  projectId,
  token,
  parentId,
  orderBy,
  order,
  searchText = "",
  notMappedYet = false,
  searchIsHidden = 2,
  signal,
}) {
  let skip = 0;
  const all = [];
  while (true) {
    const url = `${envConfig.API_ROOT}${
      envConfig.API_VERSION
    }/company-report-tool/products?project_id=${projectId}&orderBy=${orderBy}&order=${order}&limit=${FETCH_PAGE_SIZE}&skip=${skip}`;

    const result = await axios.post(
      url,
      {
        q: searchText,
        notMappedYet,
        searchIsHidden,
        parent_id: parentId == null || parentId === "" ? null : parentId,
      },
      {
        headers: {
          Authorization: token,
        },
        signal,
      }
    );
    const batch = result.data.data || [];
    all.push(...batch);
    if (batch.length < FETCH_PAGE_SIZE) {
      break;
    }
    skip += FETCH_PAGE_SIZE;
    if (skip > 1_000_000) {
      break;
    }
  }
  return sortBySortOrderThenNameThenId(all);
}

/**
 * Full project product list: body does not include `parent_id` (legacy / search mode; returns full tree or all matches).
 * Same paged contract as the original `fetchAllProjectProducts` in the tree page.
 *
 * @param {object} p
 * @param {string|number} p.projectId
 * @param {string|object} p.token
 * @param {string} p.orderBy
 * @param {string} p.order
 * @param {string} p.searchText
 * @param {boolean} p.notMappedYet
 * @param {number|string} p.searchIsHidden
 * @param {AbortSignal} [p.signal]
 */
export async function fetchAllProjectProductsFlat({
  projectId,
  token,
  orderBy,
  order,
  searchText = "",
  notMappedYet = false,
  searchIsHidden = 2,
  signal,
}) {
  let skip = 0;
  const all = [];
  while (true) {
    const url = `${envConfig.API_ROOT}${
      envConfig.API_VERSION
    }/company-report-tool/products?project_id=${projectId}&orderBy=${orderBy}&order=${order}&limit=${FETCH_PAGE_SIZE}&skip=${skip}`;
    const result = await axios.post(
      url,
      {
        q: searchText,
        notMappedYet,
        searchIsHidden,
      },
      {
        headers: {
          Authorization: token,
        },
        signal,
      }
    );
    const batch = result.data.data || [];
    all.push(...batch);
    if (batch.length < FETCH_PAGE_SIZE) {
      break;
    }
    skip += FETCH_PAGE_SIZE;
    if (skip > 1_000_000) {
      break;
    }
  }
  return all;
}
